import { useState, useEffect } from "react";
import axios from "axios";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  LayoutDashboard, Users, Building2, GitBranch, FileText, BarChart3,
  LogOut, Plus, Upload, Edit, Trash2, ChevronRight, Settings,
  CheckCircle2, Clock, XCircle, ArrowUpCircle, ArrowDownCircle, Search, Download
} from "lucide-react";
import { TIMEZONE_OPTIONS } from "../utils/timezone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPortal() {
  const { user, logout, getAuthHeaders } = useAuth();
  const location = useLocation();
  
  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
    { path: "/admin/staff", icon: Users, label: "Staff Management" },
    { path: "/admin/departments", icon: Building2, label: "Departments" },
    { path: "/admin/approval-flows", icon: GitBranch, label: "Approval Flows" },
    { path: "/admin/reports", icon: BarChart3, label: "Reports" },
    { path: "/admin/settings", icon: Settings, label: "Settings" },
  ];

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gradient-to-b from-emerald-900 to-emerald-950 text-white fixed h-screen flex flex-col">
        <div className="p-6 border-b border-white/10">
          <h1 className="font-heading font-bold text-xl">Exit Pass Admin</h1>
          <p className="text-emerald-300 text-sm">Management Portal</p>
        </div>
        
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item) => {
              const isActive = item.exact 
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive 
                        ? "bg-white/20 text-white" 
                        : "text-emerald-200 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <item.icon className="w-5 h-5" strokeWidth={1.5} />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <span className="font-bold">{user?.name?.charAt(0)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.name}</p>
              <p className="text-sm text-emerald-300 truncate">{user?.employee_id}</p>
            </div>
          </div>
          <Button
            onClick={logout}
            variant="ghost"
            data-testid="admin-logout-btn"
            className="w-full justify-start text-emerald-200 hover:text-white hover:bg-white/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        <Routes>
          <Route index element={<DashboardView />} />
          <Route path="staff" element={<StaffManagement />} />
          <Route path="departments" element={<DepartmentManagement />} />
          <Route path="approval-flows" element={<ApprovalFlowManagement />} />
          <Route path="reports" element={<ReportsView />} />
          <Route path="settings" element={<SettingsView />} />
        </Routes>
      </main>
    </div>
  );
}

// Dashboard View
function DashboardView() {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, { headers: getAuthHeaders() });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse text-primary">Loading...</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-heading font-bold text-stone-800">Dashboard</h1>
        <p className="text-stone-500">Overview of the exit pass system</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={stats.total_users || 0}
          icon={Users}
          color="primary"
        />
        <StatCard
          title="Departments"
          value={stats.total_departments || 0}
          icon={Building2}
          color="blue"
        />
        <StatCard
          title="Pending Passes"
          value={stats.pending_passes || 0}
          icon={Clock}
          color="amber"
        />
        <StatCard
          title="Approved Passes"
          value={stats.approved_passes || 0}
          icon={CheckCircle2}
          color="emerald"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-stone-200 shadow-card">
          <CardHeader>
            <CardTitle className="font-heading">Today's Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-red-50 rounded-xl p-4 text-center">
                <ArrowUpCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-2xl font-heading font-bold text-red-600">{stats.today_exits || 0}</p>
                <p className="text-sm text-red-500">Exits Today</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <ArrowDownCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-2xl font-heading font-bold text-emerald-600">{stats.today_arrivals || 0}</p>
                <p className="text-sm text-emerald-500">Arrivals Today</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-stone-200 shadow-card">
          <CardHeader>
            <CardTitle className="font-heading">Pass Status Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <StatusBar label="Pending" value={stats.pending_passes || 0} total={100} color="amber" />
              <StatusBar label="Approved" value={stats.approved_passes || 0} total={100} color="emerald" />
              <StatusBar label="Rejected" value={stats.rejected_passes || 0} total={100} color="red" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Staff Management
function StaffManagement() {
  const { getAuthHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    employee_id: "",
    name: "",
    email: "",
    password: "",
    department_id: "",
    role: "staff",
    phone: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, deptsRes] = await Promise.all([
        axios.get(`${API}/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/departments`, { headers: getAuthHeaders() }),
      ]);
      setUsers(usersRes.data);
      setDepartments(deptsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, newUser, { headers: getAuthHeaders() });
      toast.success("User created successfully");
      setShowAddDialog(false);
      setNewUser({
        employee_id: "",
        name: "",
        email: "",
        password: "",
        department_id: "",
        role: "staff",
        phone: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/users/bulk-upload`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success(`${response.data.created_count} users created`);
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      setShowUploadDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { headers: getAuthHeaders() });
      toast.success("User deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      const updateData = {
        name: editingUser.name,
        email: editingUser.email,
        department_id: editingUser.department_id,
        role: editingUser.role,
        phone: editingUser.phone,
      };
      if (editingUser.new_password) updateData.password = editingUser.new_password;
      await axios.put(`${API}/users/${editingUser.id}`, updateData, { headers: getAuthHeaders() });
      toast.success("User updated successfully");
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.employee_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const colors = {
      admin: "bg-purple-50 text-purple-700 border-purple-200",
      hr: "bg-blue-50 text-blue-700 border-blue-200",
      hod: "bg-amber-50 text-amber-700 border-amber-200",
      security: "bg-red-50 text-red-700 border-red-200",
      staff: "bg-stone-50 text-stone-700 border-stone-200",
    };
    return <Badge className={colors[role] || colors.staff}>{role.toUpperCase()}</Badge>;
  };

  if (loading) {
    return <div className="animate-pulse text-primary">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-stone-800">Staff Management</h1>
          <p className="text-stone-500">Manage resort staff accounts</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2 rounded-xl" data-testid="download-template-btn"
            onClick={() => {
              const csv = "employee_id,name,email,password,department,role,phone\nSTF001,John Smith,john.smith@resort.com,pass123,Front Office,staff,+960-7001001\nSTF002,Maria Garcia,maria.garcia@resort.com,pass123,Housekeeping,staff,+960-7001002";
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = "staff_upload_template.csv"; a.click();
              URL.revokeObjectURL(url);
            }}>
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Download</span> Template
          </Button>
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2 rounded-xl" data-testid="bulk-upload-btn">
                <Upload className="w-4 h-4" />
                Bulk Upload
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bulk Upload Staff</DialogTitle>
                <DialogDescription>Upload a CSV file with staff data. Download the template first to see the required format.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="border-2 border-dashed border-stone-200 rounded-xl p-8 text-center">
                  <Upload className="w-12 h-12 text-stone-400 mx-auto mb-4" />
                  <p className="text-sm text-stone-500 mb-2">
                    Required columns: employee_id, name, email, password, department, role, phone
                  </p>
                  <p className="text-xs text-stone-400 mb-4">
                    Roles: staff, hod, hr, security, admin
                  </p>
                  <label className="cursor-pointer">
                    <span className="btn-primary inline-block px-6 py-2 rounded-xl bg-primary text-white">
                      Select File
                    </span>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleBulkUpload}
                      className="hidden"
                      data-testid="file-upload-input"
                    />
                  </label>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 gap-2 rounded-xl" data-testid="add-user-btn">
                <Plus className="w-4 h-4" />
                Add Staff
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add New Staff</DialogTitle>
                <DialogDescription>Create a new staff account</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Employee ID *</Label>
                    <Input
                      data-testid="new-employee-id"
                      value={newUser.employee_id}
                      onChange={(e) => setNewUser({ ...newUser, employee_id: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      data-testid="new-name"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      data-testid="new-email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      data-testid="new-password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Department</Label>
                    <Select
                      value={newUser.department_id}
                      onValueChange={(v) => setNewUser({ ...newUser, department_id: v })}
                    >
                      <SelectTrigger data-testid="new-department">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map((d) => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Role *</Label>
                    <Select
                      value={newUser.role}
                      onValueChange={(v) => setNewUser({ ...newUser, role: v })}
                    >
                      <SelectTrigger data-testid="new-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="hod">HOD</SelectItem>
                        <SelectItem value="hr">HR</SelectItem>
                        <SelectItem value="security">Security</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    data-testid="new-phone"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="submit-new-user" className="bg-primary hover:bg-primary/90">
                    Create User
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-white border-stone-200 shadow-card">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Search staff..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-staff-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-mono">{user.employee_id}</TableCell>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.department_name || "-"}</TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingUser({ ...user, new_password: "" })}
                          data-testid={`edit-user-${user.id}`}
                          className="text-stone-500 hover:text-primary hover:bg-primary/5"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteUser(user.id)}
                          data-testid={`delete-user-${user.id}`}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(v) => !v && setEditingUser(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Staff — {editingUser?.employee_id}</DialogTitle>
            <DialogDescription>Update staff member details</DialogDescription>
          </DialogHeader>
          {editingUser && (
            <form onSubmit={handleEditUser} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Employee ID</Label>
                <Input value={editingUser.employee_id} disabled className="h-11 bg-stone-50 text-stone-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Name *</Label>
                <Input data-testid="edit-user-name" value={editingUser.name} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} className="h-11" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Email</Label>
                <Input data-testid="edit-user-email" type="email" value={editingUser.email || ""} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} className="h-11" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">New Password (leave blank to keep current)</Label>
                <Input data-testid="edit-user-password" type="password" value={editingUser.new_password || ""} onChange={(e) => setEditingUser({ ...editingUser, new_password: e.target.value })} placeholder="Leave blank to keep current" className="h-11" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Department</Label>
                  <Select value={editingUser.department_id || ""} onValueChange={(v) => setEditingUser({ ...editingUser, department_id: v })}>
                    <SelectTrigger data-testid="edit-user-department" className="h-11"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {departments.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Role</Label>
                  <Select value={editingUser.role} onValueChange={(v) => setEditingUser({ ...editingUser, role: v })}>
                    <SelectTrigger data-testid="edit-user-role" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["staff", "hod", "hr", "security", "admin"].map((r) => (
                        <SelectItem key={r} value={r}>{r.toUpperCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Phone</Label>
                <Input data-testid="edit-user-phone" value={editingUser.phone || ""} onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })} className="h-11" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setEditingUser(null)}>Cancel</Button>
                <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90 text-white rounded-xl" data-testid="save-user-btn">Save Changes</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Department Management
function DepartmentManagement() {
  const { getAuthHeaders } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newDept, setNewDept] = useState({ name: "", description: "" });

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`, { headers: getAuthHeaders() });
      setDepartments(response.data);
    } catch (error) {
      console.error("Error fetching departments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDepartment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/departments`, newDept, { headers: getAuthHeaders() });
      toast.success("Department created");
      setShowAddDialog(false);
      setNewDept({ name: "", description: "" });
      fetchDepartments();
    } catch (error) {
      toast.error("Failed to create department");
    }
  };

  const handleDeleteDepartment = async (deptId) => {
    if (!window.confirm("Delete this department? This will also remove its approval flow.")) return;
    try {
      await axios.delete(`${API}/departments/${deptId}`, { headers: getAuthHeaders() });
      toast.success("Department deleted");
      fetchDepartments();
    } catch (error) {
      toast.error("Failed to delete department");
    }
  };

  if (loading) {
    return <div className="animate-pulse text-primary">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-stone-800">Departments</h1>
          <p className="text-stone-500">Manage resort departments</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 gap-2 rounded-xl" data-testid="add-dept-btn">
              <Plus className="w-4 h-4" />
              Add Department
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Department</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddDepartment} className="space-y-4">
              <div className="space-y-2">
                <Label>Department Name *</Label>
                <Input
                  data-testid="dept-name"
                  value={newDept.name}
                  onChange={(e) => setNewDept({ ...newDept, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  data-testid="dept-desc"
                  value={newDept.description}
                  onChange={(e) => setNewDept({ ...newDept, description: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-dept" className="bg-primary hover:bg-primary/90">
                  Create
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {departments.map((dept) => (
          <Card key={dept.id} className="bg-white border-stone-200 shadow-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-stone-800">{dept.name}</h3>
                    <p className="text-sm text-stone-500">{dept.description || "No description"}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteDepartment(dept.id)}
                  data-testid={`delete-dept-${dept.id}`}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Approval Flow Management
function ApprovalFlowManagement() {
  const { getAuthHeaders } = useAuth();
  const [flows, setFlows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedDept, setSelectedDept] = useState("");
  const [approvalSteps, setApprovalSteps] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [flowsRes, deptsRes, usersRes] = await Promise.all([
        axios.get(`${API}/approval-flows`, { headers: getAuthHeaders() }),
        axios.get(`${API}/departments`, { headers: getAuthHeaders() }),
        axios.get(`${API}/users`, { headers: getAuthHeaders() }),
      ]);
      setFlows(flowsRes.data);
      setDepartments(deptsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFlow = async () => {
    if (!selectedDept || approvalSteps.length === 0) {
      toast.error("Please select department and add approval steps");
      return;
    }

    try {
      await axios.post(
        `${API}/approval-flows`,
        {
          department_id: selectedDept,
          approval_steps: approvalSteps,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Approval flow saved");
      setShowAddDialog(false);
      setSelectedDept("");
      setApprovalSteps([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to save approval flow");
    }
  };

  const handleEditFlow = (flow) => {
    setSelectedDept(flow.department_id);
    setApprovalSteps(flow.approval_steps.map(s => ({ ...s })));
    setShowAddDialog(true);
  };

  const handleDeleteFlow = async (flowId) => {
    if (!window.confirm("Delete this approval flow?")) return;
    try {
      await axios.delete(`${API}/approval-flows/${flowId}`, { headers: getAuthHeaders() });
      toast.success("Approval flow deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete approval flow");
    }
  };

  const addStep = () => {
    setApprovalSteps([...approvalSteps, { role: "", user_id: "", user_name: "" }]);
  };

  const updateStep = (index, field, value) => {
    const updated = [...approvalSteps];
    updated[index][field] = value;
    if (field === "user_id") {
      const user = users.find((u) => u.id === value);
      if (user) {
        updated[index].user_name = user.name;
      }
    }
    setApprovalSteps(updated);
  };

  const removeStep = (index) => {
    setApprovalSteps(approvalSteps.filter((_, i) => i !== index));
  };

  if (loading) {
    return <div className="animate-pulse text-primary">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-stone-800">Approval Flows</h1>
          <p className="text-stone-500">Configure approval workflows for each department</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 gap-2 rounded-xl" data-testid="add-flow-btn">
              <Plus className="w-4 h-4" />
              Configure Flow
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Configure Approval Flow</DialogTitle>
              <DialogDescription>Set up the approval chain for a department</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Department *</Label>
                <Select value={selectedDept} onValueChange={setSelectedDept}>
                  <SelectTrigger data-testid="flow-dept-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Approval Steps</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addStep} data-testid="add-step-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Step
                  </Button>
                </div>
                
                {approvalSteps.map((step, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-stone-50 rounded-xl">
                    <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
                      {idx + 1}
                    </span>
                    <Select
                      value={step.role}
                      onValueChange={(v) => updateStep(idx, "role", v)}
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue placeholder="Role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hod">HOD</SelectItem>
                        <SelectItem value="hr">HR</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={step.user_id}
                      onValueChange={(v) => updateStep(idx, "user_id", v)}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select approver" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => ["hod", "hr", "admin"].includes(u.role)).map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name} ({u.role.toUpperCase()})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeStep(idx)}
                      className="text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveFlow} data-testid="save-flow-btn" className="bg-primary hover:bg-primary/90">
                  Save Flow
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {flows.length === 0 ? (
          <Card className="border-dashed border-2 border-stone-200">
            <CardContent className="py-12 text-center">
              <GitBranch className="w-12 h-12 text-stone-400 mx-auto mb-4" />
              <p className="text-stone-500">No approval flows configured yet</p>
            </CardContent>
          </Card>
        ) : (
          flows.map((flow) => (
            <Card key={flow.id} className="bg-white border-stone-200 shadow-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-heading font-semibold text-stone-800">{flow.department_name}</h3>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEditFlow(flow)} className="text-stone-500 hover:text-primary" data-testid={`edit-flow-${flow.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteFlow(flow.id)} className="text-stone-500 hover:text-red-600" data-testid={`delete-flow-${flow.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {flow.approval_steps.map((step, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="bg-stone-100 rounded-lg px-3 py-2 flex items-center gap-2">
                        <span className="w-5 h-5 bg-primary text-white rounded-full flex items-center justify-center text-xs">
                          {idx + 1}
                        </span>
                        <span className="text-sm font-medium">{step.user_name || step.role}</span>
                        <Badge variant="outline" className="text-xs">{step.role}</Badge>
                      </div>
                      {idx < flow.approval_steps.length - 1 && (
                        <ChevronRight className="w-4 h-4 text-stone-400" />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

// Reports View
function ReportsView() {
  const { getAuthHeaders } = useAuth();
  const [movements, setMovements] = useState([]);
  const [exitPassReports, setExitPassReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [purposeFilter, setPurposeFilter] = useState("");
  const [purposeOptions, setPurposeOptions] = useState(["Overnight", "Off Days", "Annual Leave", "Medical", "Other"]);
  const [reportTab, setReportTab] = useState("movements");

  useEffect(() => {
    fetchReport();
    axios.get(`${API}/settings`, { headers: getAuthHeaders() }).then(res => {
      if (res.data?.purpose_options) {
        setPurposeOptions(typeof res.data.purpose_options === 'string' ? JSON.parse(res.data.purpose_options) : res.data.purpose_options);
      }
    }).catch(() => {});
  }, []);

  const fetchReport = async () => {
    try {
      let url = `${API}/reports/movements`;
      const params = [];
      if (dateFrom) params.push(`date_from=${dateFrom}`);
      if (dateTo) params.push(`date_to=${dateTo}`);
      if (purposeFilter && purposeFilter !== "all") params.push(`purpose=${encodeURIComponent(purposeFilter)}`);
      if (params.length) url += `?${params.join("&")}`;

      const response = await axios.get(url, { headers: getAuthHeaders() });
      setMovements(response.data);
    } catch (error) {
      console.error("Error fetching report:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExitPassReports = async () => {
    try {
      let url = `${API}/reports/exit-passes`;
      const params = [];
      if (dateFrom) params.push(`date_from=${dateFrom}`);
      if (dateTo) params.push(`date_to=${dateTo}`);
      if (purposeFilter && purposeFilter !== "all") params.push(`purpose=${encodeURIComponent(purposeFilter)}`);
      if (params.length) url += `?${params.join("&")}`;

      const response = await axios.get(url, { headers: getAuthHeaders() });
      setExitPassReports(response.data);
    } catch (error) {
      console.error("Error fetching exit pass report:", error);
    }
  };

  const handleApplyFilter = () => {
    if (reportTab === "movements") fetchReport();
    else fetchExitPassReports();
  };

  const exportToCSV = (type) => {
    let csvContent = "";
    let filename = "";
    if (type === "movements") {
      csvContent = "Date/Time,Staff,Employee ID,Department,Type,Recorded By\n";
      (movements.movements || []).forEach(m => {
        csvContent += `"${m.recorded_at?.replace("T"," ").slice(0,16)}","${m.staff_name}",${m.employee_id},"${m.department_name || ""}",${m.movement_type === "exit" ? "Exit" : "Arrival"},"${m.security_name}"\n`;
      });
      filename = `movements_report_${new Date().toISOString().slice(0,10)}.csv`;
    } else {
      csvContent = "Status,Staff,Employee ID,Department,Purpose,Destination,Departure Date,Departure Time,Arrival Date,Arrival Time\n";
      exitPassReports.forEach(p => {
        csvContent += `${p.status},"${p.staff_name}",${p.employee_id},"${p.department_name || ""}","${p.purpose}","${p.destination}",${p.departure_date},${p.departure_time},${p.arrival_date},${p.arrival_time}\n`;
      });
      filename = `exit_passes_report_${new Date().toISOString().slice(0,10)}.csv`;
    }
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("CSV exported successfully");
  };

  const exportToPDF = async (type) => {
    const { default: jsPDF } = await import("jspdf");
    await import("jspdf-autotable");
    const doc = new jsPDF({ orientation: "landscape" });
    const title = type === "movements" ? "Staff Movement Report" : "Exit Pass Report";
    const dateStr = new Date().toLocaleDateString();

    doc.setFontSize(18);
    doc.text(title, 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${dateStr}${dateFrom ? ` | From: ${dateFrom}` : ""}${dateTo ? ` | To: ${dateTo}` : ""}${purposeFilter && purposeFilter !== "all" ? ` | Purpose: ${purposeFilter}` : ""}`, 14, 28);

    if (type === "movements") {
      const headers = [["Date/Time", "Staff", "Employee ID", "Department", "Type", "Recorded By"]];
      const rows = (movements.movements || []).map(m => [
        m.recorded_at?.replace("T"," ").slice(0,16), m.staff_name, m.employee_id,
        m.department_name || "—", m.movement_type === "exit" ? "Exit" : "Arrival", m.security_name
      ]);
      doc.autoTable({ head: headers, body: rows, startY: 34, styles: { fontSize: 9 }, headStyles: { fillColor: [30, 70, 50] } });
      doc.setFontSize(9);
      doc.text(`Total: ${(movements.movements || []).length} record(s) | Exits: ${movements.total_exits || 0} | Arrivals: ${movements.total_arrivals || 0}`, 14, doc.lastAutoTable.finalY + 10);
    } else {
      const headers = [["Status", "Staff", "Employee ID", "Department", "Purpose", "Destination", "Departure", "Arrival"]];
      const rows = exitPassReports.map(p => [
        p.status, p.staff_name, p.employee_id, p.department_name || "—",
        p.purpose, p.destination, `${p.departure_date} ${p.departure_time}`, `${p.arrival_date} ${p.arrival_time}`
      ]);
      doc.autoTable({ head: headers, body: rows, startY: 34, styles: { fontSize: 9 }, headStyles: { fillColor: [30, 70, 50] } });
      doc.setFontSize(9);
      doc.text(`Total: ${exitPassReports.length} record(s)`, 14, doc.lastAutoTable.finalY + 10);
    }
    doc.save(`${type === "movements" ? "movements" : "exit_passes"}_report_${new Date().toISOString().slice(0,10)}.pdf`);
    toast.success("PDF exported successfully");
  };

  if (loading) {
    return <div className="animate-pulse text-primary">Loading...</div>;
  }

  const movementList = movements.movements || [];
  const hasData = reportTab === "movements" ? movementList.length > 0 : exitPassReports.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-bold text-stone-800">Reports</h1>
        <p className="text-stone-500">Staff movement reports and analytics</p>
      </div>

      <Card className="bg-white border-stone-200 shadow-card">
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>From Date</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from" />
            </div>
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to" />
            </div>
            <div className="space-y-2">
              <Label>Purpose</Label>
              <Select value={purposeFilter} onValueChange={setPurposeFilter}>
                <SelectTrigger className="w-44" data-testid="admin-report-purpose-filter">
                  <SelectValue placeholder="All Purposes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Purposes</SelectItem>
                  {purposeOptions.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleApplyFilter} data-testid="apply-filter" className="bg-primary hover:bg-primary/90">
              Apply Filter
            </Button>
            <Button variant="outline" onClick={() => exportToCSV(reportTab)} disabled={!hasData} data-testid="admin-export-csv">
              <Download className="w-4 h-4 mr-1.5" /> CSV
            </Button>
            <Button variant="outline" onClick={() => exportToPDF(reportTab)} disabled={!hasData} data-testid="admin-export-pdf">
              <Download className="w-4 h-4 mr-1.5" /> PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Type Tabs */}
      <div className="flex gap-2">
        <Button
          variant={reportTab === "movements" ? "default" : "outline"}
          onClick={() => { setReportTab("movements"); fetchReport(); }}
          className={reportTab === "movements" ? "bg-primary" : ""}
          data-testid="admin-movements-tab"
        >
          Movements
        </Button>
        <Button
          variant={reportTab === "exit-passes" ? "default" : "outline"}
          onClick={() => { setReportTab("exit-passes"); fetchExitPassReports(); }}
          className={reportTab === "exit-passes" ? "bg-primary" : ""}
          data-testid="admin-exitpasses-tab"
        >
          Exit Passes
        </Button>
      </div>

      {/* Movements View */}
      {reportTab === "movements" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-white border-stone-200 shadow-card">
              <CardContent className="p-6 text-center">
                <p className="text-4xl font-heading font-bold text-stone-800">{movements.total_movements || 0}</p>
                <p className="text-stone-500">Total Movements</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-6 text-center">
                <p className="text-4xl font-heading font-bold text-red-600">{movements.total_exits || 0}</p>
                <p className="text-red-500">Total Exits</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-6 text-center">
                <p className="text-4xl font-heading font-bold text-emerald-600">{movements.total_arrivals || 0}</p>
                <p className="text-emerald-500">Total Arrivals</p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-white border-stone-200 shadow-card">
            <CardHeader>
              <CardTitle>Movement Log</CardTitle>
              <CardDescription>{movementList.length} record(s)</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date/Time</TableHead>
                      <TableHead>Staff</TableHead>
                      <TableHead>Employee ID</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Recorded By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {movementList.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell className="font-mono text-sm">{m.recorded_at?.replace("T", " ").slice(0, 16)}</TableCell>
                        <TableCell className="font-medium">{m.staff_name}</TableCell>
                        <TableCell className="font-mono">{m.employee_id}</TableCell>
                        <TableCell>{m.department_name || "-"}</TableCell>
                        <TableCell>
                          {m.movement_type === "exit" ? (
                            <Badge className="bg-red-50 text-red-700 border-red-200">Exit</Badge>
                          ) : (
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Arrival</Badge>
                          )}
                        </TableCell>
                        <TableCell>{m.security_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </>
      )}

      {/* Exit Passes View */}
      {reportTab === "exit-passes" && (
        <Card className="bg-white border-stone-200 shadow-card">
          <CardHeader>
            <CardTitle>Exit Pass Records</CardTitle>
            <CardDescription>{exitPassReports.length} record(s)</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Staff</TableHead>
                    <TableHead>Employee ID</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Purpose</TableHead>
                    <TableHead>Destination</TableHead>
                    <TableHead>Departure</TableHead>
                    <TableHead>Arrival</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exitPassReports.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <Badge className={
                          p.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                          p.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" :
                          p.status === "used" ? "bg-blue-50 text-blue-700 border-blue-200" :
                          "bg-red-50 text-red-700 border-red-200"
                        }>
                          {p.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{p.staff_name}</TableCell>
                      <TableCell className="font-mono">{p.employee_id}</TableCell>
                      <TableCell>{p.department_name || "-"}</TableCell>
                      <TableCell><Badge variant="outline">{p.purpose}</Badge></TableCell>
                      <TableCell>{p.destination}</TableCell>
                      <TableCell className="text-sm">{p.departure_date} {p.departure_time}</TableCell>
                      <TableCell className="text-sm">{p.arrival_date} {p.arrival_time}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Helper Components
const StatCard = ({ title, value, icon: Icon, color }) => {
  const colors = {
    primary: "bg-primary/10 text-primary",
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
    emerald: "bg-emerald-50 text-emerald-600",
  };

  return (
    <Card className="bg-white border-stone-200 shadow-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-stone-500 font-medium">{title}</p>
            <p className="text-3xl font-heading font-bold text-stone-800">{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]}`}>
            <Icon className="w-6 h-6" strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const StatusBar = ({ label, value, total, color }) => {
  const colors = {
    amber: "bg-amber-500",
    emerald: "bg-emerald-500",
    red: "bg-red-500",
  };
  const percent = total > 0 ? (value / total) * 100 : 0;

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-stone-600">{label}</span>
        <span className="font-medium">{value}</span>
      </div>
      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
        <div className={`h-full ${colors[color]} rounded-full`} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

// Settings View
function SettingsView() {
  const { getAuthHeaders } = useAuth();
  const [settings, setSettings] = useState({ staff_can_add_belongings: false, purpose_options: [], timezone: "Asia/Dhaka" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newPurpose, setNewPurpose] = useState("");
  const [editingPurpose, setEditingPurpose] = useState(null);
  const [editPurposeVal, setEditPurposeVal] = useState("");
  const [uploadingHr, setUploadingHr] = useState(false);
  const [pendingTimezone, setPendingTimezone] = useState(null);
  const [currentTime, setCurrentTime] = useState("");

  useEffect(() => { fetchSettings(); }, []);

  // Live clock for selected timezone
  useEffect(() => {
    const tz = pendingTimezone || settings.timezone || "Asia/Dhaka";
    const updateClock = () => {
      try {
        const gmtMatch = tz.match(/^GMT([+-]\d+)$/);
        if (gmtMatch) {
          const off = parseInt(gmtMatch[1]);
          const now = new Date();
          const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
          const local = new Date(utcMs + off * 3600000);
          setCurrentTime(local.toLocaleString("en-GB", { weekday: "short", year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }));
        } else {
          setCurrentTime(new Intl.DateTimeFormat("en-GB", {
            timeZone: tz, weekday: "short", year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false
          }).format(new Date()));
        }
      } catch { setCurrentTime(new Date().toLocaleString()); }
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, [pendingTimezone, settings.timezone]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`, { headers: getAuthHeaders() });
      setSettings(res.data);
    } catch (error) {
      console.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const toggleSetting = async (key) => {
    const newValue = !settings[key];
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, { [key]: newValue }, { headers: getAuthHeaders() });
      setSettings(prev => ({ ...prev, [key]: newValue }));
      toast.success("Setting updated");
    } catch (error) {
      toast.error("Failed to update setting");
    } finally { setSaving(false); }
  };

  const applyTimezone = async () => {
    if (!pendingTimezone) return;
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, { timezone: pendingTimezone }, { headers: getAuthHeaders() });
      setSettings(prev => ({ ...prev, timezone: pendingTimezone }));
      setPendingTimezone(null);
      toast.success("Timezone applied successfully");
    } catch (error) {
      toast.error("Failed to update timezone");
    } finally { setSaving(false); }
  };

  const addPurpose = async () => {
    if (!newPurpose.trim()) return;
    const updated = [...(settings.purpose_options || []), newPurpose.trim()];
    try {
      await axios.put(`${API}/settings`, { purpose_options: updated }, { headers: getAuthHeaders() });
      setSettings(prev => ({ ...prev, purpose_options: updated }));
      setNewPurpose("");
      toast.success("Purpose option added");
    } catch { toast.error("Failed to add"); }
  };

  const updatePurpose = async (idx) => {
    if (!editPurposeVal.trim()) return;
    const updated = [...(settings.purpose_options || [])];
    updated[idx] = editPurposeVal.trim();
    try {
      await axios.put(`${API}/settings`, { purpose_options: updated }, { headers: getAuthHeaders() });
      setSettings(prev => ({ ...prev, purpose_options: updated }));
      setEditingPurpose(null);
      toast.success("Purpose option updated");
    } catch { toast.error("Failed to update"); }
  };

  const removePurpose = async (idx) => {
    const updated = (settings.purpose_options || []).filter((_, i) => i !== idx);
    try {
      await axios.put(`${API}/settings`, { purpose_options: updated }, { headers: getAuthHeaders() });
      setSettings(prev => ({ ...prev, purpose_options: updated }));
      toast.success("Purpose option removed");
    } catch { toast.error("Failed to remove"); }
  };

  const handleHrUpload = async (e, listType, mode) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHr(true);
    
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) {
        toast.error("CSV file is empty or has no data rows");
        setUploadingHr(false);
        e.target.value = "";
        return;
      }
      
      // Parse CSV header
      const parseCSVLine = (line) => {
        const result = [];
        let current = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          if (line[i] === '"') { inQuotes = !inQuotes; }
          else if (line[i] === ',' && !inQuotes) { result.push(current.trim()); current = ""; }
          else { current += line[i]; }
        }
        result.push(current.trim());
        return result;
      };
      
      const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase());
      
      // Map column indices
      const col = (name) => headers.indexOf(name);
      const idIdx = col("id_no");
      const nameIdx = col("emp_name");
      const divIdx = col("div_name");
      const depIdx = col("dep_name");
      const posIdx = col("pos_name");
      const purposeIdx = col("purpose");
      const contactIdx = col("contactno");
      const destIdx = col("destination");
      const travelIdx = col("traveltype");
      const statusIdx = col("psastatus");
      
      // Date/time/flight based on selected list type
      const depFlightIdx = col("departureflight");
      const depDateIdx = col("departuredate");
      const depTimeIdx = col("departuretime");
      const arrFlightIdx = col("arrivalflight");
      const arrDateIdx = col("returndate");
      const arrTimeIdx = col("arrivaltime");
      
      const flightIdx = listType === "departure" ? depFlightIdx : arrFlightIdx;
      const dateIdx = listType === "departure" ? depDateIdx : arrDateIdx;
      const timeIdx = listType === "departure" ? depTimeIdx : arrTimeIdx;
      
      if (idIdx === -1) {
        toast.error("CSV missing required 'id_no' column");
        setUploadingHr(false);
        e.target.value = "";
        return;
      }
      
      // Parse date: "08/03/2026 00:00:00" → "2026-03-08"
      const parseDate = (val) => {
        if (!val) return "";
        const match = val.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
        if (match) return `${match[3]}-${match[2]}-${match[1]}`;
        return val;
      };
      // Parse time: "01/01/1900 09:00:00" → "09:00"
      const parseTime = (val) => {
        if (!val) return "";
        const match = val.match(/(\d{2}:\d{2})(:\d{2})?$/);
        if (match) return match[1];
        return val;
      };
      const getVal = (fields, idx) => idx >= 0 && idx < fields.length ? fields[idx] : "";
      
      const records = [];
      for (let i = 1; i < lines.length; i++) {
        const fields = parseCSVLine(lines[i]);
        const empId = getVal(fields, idIdx);
        const name = getVal(fields, nameIdx);
        if (!empId || !name) continue;
        
        records.push({
          employee_id: empId,
          name: name,
          department: getVal(fields, depIdx),
          division: getVal(fields, divIdx),
          position: getVal(fields, posIdx),
          purpose: getVal(fields, purposeIdx),
          contact: getVal(fields, contactIdx),
          destination: getVal(fields, destIdx),
          travel_req: getVal(fields, travelIdx),
          flight: getVal(fields, flightIdx),
          date: parseDate(getVal(fields, dateIdx)),
          time: parseTime(getVal(fields, timeIdx)),
          status: getVal(fields, statusIdx)
        });
      }
      
      if (records.length === 0) {
        toast.error("No valid staff records found in the CSV");
        setUploadingHr(false);
        e.target.value = "";
        return;
      }
      
      const res = await axios.post(`${API}/hr-lists/upload-json?list_type=${listType}&mode=${mode}`, 
        { records },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingHr(false);
      e.target.value = "";
    }
  };

  if (loading) return <div className="animate-pulse text-stone-400">Loading settings...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-bold text-2xl text-stone-800">Settings</h1>
        <p className="text-stone-500 mt-1">Configure system-wide settings</p>
      </div>

      {/* Timezone */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Timezone</CardTitle>
          <CardDescription>Set the timezone for all dates and times displayed in the system</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Live Clock Preview */}
          <div className="bg-stone-900 rounded-xl p-4 text-center">
            <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Current Time</p>
            <p className="text-2xl font-mono font-bold text-white" data-testid="current-time-display">{currentTime}</p>
            <p className="text-xs text-stone-500 mt-1">{pendingTimezone || settings.timezone || "Asia/Dhaka"}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={pendingTimezone || settings.timezone || "Asia/Dhaka"} onValueChange={(v) => setPendingTimezone(v)}>
              <SelectTrigger data-testid="timezone-select" className="h-11 flex-1"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                {TIMEZONE_OPTIONS.map(tz => (
                  <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={applyTimezone} disabled={saving || !pendingTimezone} data-testid="apply-timezone-btn"
              className={`rounded-xl h-11 px-6 ${pendingTimezone ? "bg-primary hover:bg-primary/90 text-white" : "bg-stone-200 text-stone-400"}`}>
              {saving ? "Applying..." : "Apply"}
            </Button>
          </div>
          {pendingTimezone && pendingTimezone !== settings.timezone && (
            <p className="text-xs text-amber-600">Timezone changed to {pendingTimezone} — click Apply to save.</p>
          )}
        </CardContent>
      </Card>

      {/* Purpose Options */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Exit Pass Purpose Options</CardTitle>
          <CardDescription>Manage the dropdown options available when staff create exit pass requests</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            {(settings.purpose_options || []).map((opt, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
                {editingPurpose === idx ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input value={editPurposeVal} onChange={(e) => setEditPurposeVal(e.target.value)} className="h-9" data-testid={`edit-purpose-input-${idx}`} />
                    <Button size="sm" onClick={() => updatePurpose(idx)} className="bg-primary text-white rounded-lg">Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingPurpose(null)} className="rounded-lg">Cancel</Button>
                  </div>
                ) : (
                  <>
                    <span className="font-medium text-stone-700">{opt}</span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingPurpose(idx); setEditPurposeVal(opt); }} data-testid={`edit-purpose-${idx}`}>
                        <Edit className="w-4 h-4 text-stone-400" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => removePurpose(idx)} data-testid={`delete-purpose-${idx}`}>
                        <Trash2 className="w-4 h-4 text-stone-400 hover:text-red-500" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newPurpose} onChange={(e) => setNewPurpose(e.target.value)} placeholder="New purpose option..." className="h-10" data-testid="new-purpose-input"
              onKeyDown={(e) => e.key === "Enter" && addPurpose()} />
            <Button onClick={addPurpose} className="bg-primary text-white rounded-lg" data-testid="add-purpose-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Belongings Management */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Belongings Management</CardTitle>
          <CardDescription>Control who can manage staff belongings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
            <div>
              <p className="font-semibold text-stone-800">Allow staff to add their own belongings</p>
              <p className="text-sm text-stone-500 mt-0.5">When enabled, staff can manage their own belongings. When disabled, only security can manage.</p>
            </div>
            <button onClick={() => toggleSetting("staff_can_add_belongings")} disabled={saving} data-testid="toggle-staff-belongings"
              className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ml-4 ${settings.staff_can_add_belongings ? "bg-emerald-500" : "bg-stone-300"}`}>
              <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.staff_can_add_belongings ? "translate-x-7" : "translate-x-0.5"}`} />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* HR List Upload */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg">HR List Upload</CardTitle>
          <CardDescription>Upload daily departure and arrival lists from the HR system (.csv files)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 bg-red-50 rounded-xl space-y-3">
              <p className="font-semibold text-red-700 flex items-center gap-2"><ArrowUpCircle className="w-4 h-4" /> Departure List</p>
              <div className="space-y-2">
                <Label className="text-xs text-red-600">Upload & Keep Existing Data</Label>
                <label className="flex items-center gap-2 cursor-pointer bg-white border border-red-200 rounded-lg p-2 hover:bg-red-50 transition-colors">
                  <Upload className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-700">{uploadingHr ? "Uploading..." : "Upload CSV (Keep)"}</span>
                  <input type="file" accept=".csv" className="hidden" disabled={uploadingHr} onChange={(e) => handleHrUpload(e, "departure", "keep")} data-testid="upload-departure-keep" />
                </label>
                <label className="flex items-center gap-2 cursor-pointer bg-white border border-red-200 rounded-lg p-2 hover:bg-red-50 transition-colors">
                  <Trash2 className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-700">{uploadingHr ? "Uploading..." : "Upload CSV (Replace All)"}</span>
                  <input type="file" accept=".csv" className="hidden" disabled={uploadingHr} onChange={(e) => handleHrUpload(e, "departure", "replace")} data-testid="upload-departure-replace" />
                </label>
              </div>
            </div>
            <div className="p-4 bg-emerald-50 rounded-xl space-y-3">
              <p className="font-semibold text-emerald-700 flex items-center gap-2"><ArrowDownCircle className="w-4 h-4" /> Arrival List</p>
              <div className="space-y-2">
                <Label className="text-xs text-emerald-600">Upload & Keep Existing Data</Label>
                <label className="flex items-center gap-2 cursor-pointer bg-white border border-emerald-200 rounded-lg p-2 hover:bg-emerald-50 transition-colors">
                  <Upload className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm text-emerald-700">{uploadingHr ? "Uploading..." : "Upload CSV (Keep)"}</span>
                  <input type="file" accept=".csv" className="hidden" disabled={uploadingHr} onChange={(e) => handleHrUpload(e, "arrival", "keep")} data-testid="upload-arrival-keep" />
                </label>
                <label className="flex items-center gap-2 cursor-pointer bg-white border border-emerald-200 rounded-lg p-2 hover:bg-emerald-50 transition-colors">
                  <Trash2 className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm text-emerald-700">{uploadingHr ? "Uploading..." : "Upload CSV (Replace All)"}</span>
                  <input type="file" accept=".csv" className="hidden" disabled={uploadingHr} onChange={(e) => handleHrUpload(e, "arrival", "replace")} data-testid="upload-arrival-replace" />
                </label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
